import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Alert from '@/components/ui/Alert';
import authApi from '@/services/auth';
import { ApiClientError } from '@/services/api';
import { getUserFriendlyMessage, getRateLimitMessage } from '@/utils/errorMessages';

export interface MagicLinkFormProps {
  onSuccess?: () => void;
  className?: string;
}

/**
 * Magic Link Form Component
 * Allows users to request a passwordless login link via email
 */
export default function MagicLinkForm({ onSuccess, className }: MagicLinkFormProps) {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authApi.requestMagicLink(email);
      setSuccess(true);
      onSuccess?.();
    } catch (err) {
      // Handle specific error cases with user-friendly messages
      if (err instanceof ApiClientError) {
        // Handle specific cases that need custom messages
        if (err.status === 503 || err.status === 502) {
          // Service unavailable - email service not configured or down
          setError('Email service is temporarily unavailable. Please try another sign-in method.');
        } else if (err.status === 429) {
          // Rate limited - use helper that includes retry time if available
          setError(getRateLimitMessage(err.retryAfter));
        } else if (err.status === 404) {
          // Account not found - keep specific message
          setError('No account found with this email address. Please check your email or sign up.');
        } else if (err.category === 'validation') {
          // Validation errors - show the actual error detail
          setError(err.detail || getUserFriendlyMessage(err.category));
        } else {
          // Use standard user-friendly message for other errors
          const errorMessage = err.detail || err.message;
          if (errorMessage?.toLowerCase().includes('email service') ||
              errorMessage?.toLowerCase().includes('not configured') ||
              errorMessage?.toLowerCase().includes('email_not_enabled')) {
            setError('Email service is temporarily unavailable. Please try another sign-in method.');
          } else {
            setError(getUserFriendlyMessage(err.category, err.requestId));
          }
        }
      } else {
        // Generic error handling
        const errorMessage = err instanceof Error ? err.message : 'Failed to send magic link';
        setError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className={className}>
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
            <svg
              className="h-6 w-6 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Check your email
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            We sent a sign-in link to <strong className="font-medium">{email}</strong>
          </p>
          <p className="text-sm text-gray-500 mb-6">
            Click the link in the email to sign in. The link will expire in 15 minutes.
          </p>
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              className="text-sm font-medium text-primary-600 hover:text-primary-500"
            >
              Use a different email address
            </button>
            <div className="pt-2">
              <Link to="/login">
                <Button variant="outline" className="w-full">
                  Back to sign in
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="text-center mb-6">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-primary-100 mb-4">
          <svg
            className="h-6 w-6 text-primary-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Sign in with magic link
        </h3>
        <p className="text-sm text-gray-600">
          Enter your email and we will send you a link to sign in instantly, no password required.
        </p>
      </div>

      {error && (
        <Alert variant="error" className="mb-4" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Email address"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          autoFocus
          required
        />

        <Button type="submit" className="w-full" isLoading={isLoading}>
          Send magic link
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-gray-600">
        Prefer to use a password?{' '}
        <Link
          to="/login"
          className="font-medium text-primary-600 hover:text-primary-500"
        >
          Sign in with password
        </Link>
      </p>
    </div>
  );
}
